// FIX-CCS-C-CAPTURE-TIME (CCS Offline Part C, unit C1 — app half): the phone's capture-time CLAIMS (§6.1).
//
// The phone never decides the capture time. It states what it knows, once, at two moments:
//   at PICK (the shutter or the gallery pick):   picked_at_client, captured_at_client (the file's own time),
//                                                whether the app was offline, and the last measured clock gap
//                                                (client_offset_before_s, from the server_now the A2 plumbing records)
//   at SEND:                                     client_now_at_send (the phone clock), client_install_id (one random id
//                                                per install), client_seq (a counter that goes up by one per photo on
//                                                this install), offline_evidence (offline at pick OR a send attempt
//                                                for this photo failed), context_as_of ('send' on this live path:
//                                                cycle/crop/stage are looked up at send; the Stage D outbox will say
//                                                'capture' when it stores them at pick).
// The server stores these as sent and the DB trust rule (fn_photos_capture_time) judges them against the server-read
// Exif and the arrival time; every field is optional on the wire, and an old build that sends none of them is 'no_claim'.

import { getServerClockGap } from '@/shared/lib/serverClock';

const INSTALL_ID_KEY = 'agxl_client_install_id';
const SEQ_KEY = 'agxl_client_photo_seq';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What the phone knew at pick time. Kept with the picked file until it is sent. */
export interface CaptureClaim {
  /** ISO — when the photo reached the app (shutter or gallery pick). */
  picked_at_client: string;
  /** ISO — the file's own modification time (a fresh camera capture: ≈ now; a gallery pick: when it was taken), or null. */
  captured_at_client: string | null;
  /** The app was offline at pick. */
  offline_at_pick: boolean;
  /** Phone-minus-server clock gap in whole seconds at the last server contact BEFORE this pick, or null if never measured. */
  client_offset_before_s: number | null;
}

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // RFC 4122 v4 from getRandomValues (very old WebViews)
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

let memoInstallId: string | null = null;

/** One random id per app install, created the first time it is needed and kept in localStorage. */
export function getClientInstallId(): string {
  if (memoInstallId) return memoInstallId;
  let id: string | null = null;
  try {
    id = localStorage.getItem(INSTALL_ID_KEY);
  } catch {
    id = null;
  }
  if (!id || !UUID_RE.test(id)) {
    id = randomUuid();
    try {
      localStorage.setItem(INSTALL_ID_KEY, id);
    } catch {
      /* storage blocked — the in-memory id lasts for this run */
    }
  }
  memoInstallId = id;
  return id;
}

let memoSeq: number | null = null;

/** The next photo sequence number on this install (1, 2, 3 … persisted; never reused). */
export function nextClientSeq(): number {
  let cur = memoSeq;
  if (cur === null) {
    try {
      const raw = localStorage.getItem(SEQ_KEY);
      const n = raw === null ? 0 : Number(raw);
      cur = Number.isInteger(n) && n >= 0 ? n : 0;
    } catch {
      cur = 0;
    }
  }
  const next = cur + 1;
  memoSeq = next;
  try {
    localStorage.setItem(SEQ_KEY, String(next));
  } catch {
    /* storage blocked — the in-memory counter still goes up */
  }
  return next;
}

/** Record what the phone knows the moment a file is picked. */
export function recordPick(file: File | Blob, nowMs: number = Date.now()): CaptureClaim {
  const lastModified = 'lastModified' in file && typeof (file as File).lastModified === 'number' ? (file as File).lastModified : NaN;
  const gap = getServerClockGap();
  return {
    picked_at_client: new Date(nowMs).toISOString(),
    captured_at_client: Number.isFinite(lastModified) && lastModified > 0 ? new Date(lastModified).toISOString() : null,
    offline_at_pick: typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? !navigator.onLine : false,
    client_offset_before_s: gap ? Math.round(gap.gap_ms / 1000) : null,
  };
}

export interface SendOptions {
  /** A previous send attempt for this same photo failed (kept on the phone as offline evidence). */
  sendFailedBefore?: boolean;
  /** 'capture' when cycle/crop/stage were stored at pick (outbox); 'send' when they are looked up now (this live path). */
  contextAsOf?: 'capture' | 'send';
  nowMs?: number;
  /** D (outbox): the install id and sequence number frozen into the item at save. Absent → assigned now (the live path). */
  frozenSeq?: { client_install_id: string; client_seq: number };
  /** D (outbox): when the item was written to the outbox (ISO). Sent as client_queued_at. */
  clientQueuedAt?: string;
}

/** The wire fields for one send, as upload-photo reads them (see supabase/functions/upload-photo readCaptureClaims). */
export function captureClaimFields(claim: CaptureClaim, opts: SendOptions = {}): Record<string, string> {
  const nowMs = opts.nowMs ?? Date.now();
  const fields: Record<string, string> = {
    picked_at_client: claim.picked_at_client,
    client_install_id: opts.frozenSeq?.client_install_id ?? getClientInstallId(),
    client_seq: String(opts.frozenSeq?.client_seq ?? nextClientSeq()),
    client_now_at_send: new Date(nowMs).toISOString(),
    offline_evidence: claim.offline_at_pick || opts.sendFailedBefore === true ? 'true' : 'false',
    context_as_of: opts.contextAsOf ?? 'send',
  };
  if (claim.captured_at_client) fields.captured_at_client = claim.captured_at_client;
  if (claim.client_offset_before_s !== null) fields.client_offset_before_s = String(claim.client_offset_before_s);
  if (opts.clientQueuedAt) fields.client_queued_at = opts.clientQueuedAt;
  return fields;
}

/** Append the claims to the upload form. Absent claim (no pick recorded) → nothing appended → the server sees 'no_claim'. */
export function appendCaptureClaims(formData: FormData, claim: CaptureClaim | null, opts: SendOptions = {}): void {
  if (!claim) return;
  for (const [k, v] of Object.entries(captureClaimFields(claim, opts))) formData.append(k, v);
}
